/**
 * Ticket KN1: Pflege der Keynotes.
 *
 * Zwei Listen an einer Stelle: die Referenten (Verweis auf ein Teamprofil,
 * Reihenfolge, Sichtbarkeit) und darunter je Referent die Themen mit
 * Überschrift, Text, Reihenfolge und Sichtbarkeit.
 *
 * Bewusst kein Feld am Profil selbst: Ein Thema braucht eine eigene Reihenfolge
 * und später möglicherweise ein Bild. Bewusst keine zweite Personenverwaltung:
 * Foto, Name, Funktion und Vorstellungstext bleiben am Teamprofil.
 *
 * „Zurückgezogen" ist der Weg, ein Thema aus der Seite zu nehmen, ohne es zu
 * löschen.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { listPeopleOptions } from "@/lib/team-people.functions";
import {
  deleteKeynoteSpeaker,
  deleteKeynoteTopic,
  listKeynoteSpeakersForAdmin,
  saveKeynoteSpeaker,
  saveKeynoteTopic,
} from "@/lib/keynotes.functions";
import { KEYNOTE_STATUSES, KEYNOTE_STATUS_LABELS, type KeynoteStatus } from "@/lib/keynotes";
import { personName } from "@/lib/team-people";

export const Route = createFileRoute("/_admin/admin/keynotes")({
  component: AdminKeynotesPage,
});

type SpeakerInput = {
  id?: string;
  profileId: string;
  sortOrder: number;
  status: KeynoteStatus;
};

type TopicInput = {
  id?: string;
  speakerId: string;
  heading: string;
  bodyMd: string;
  sortOrder: number;
  status: KeynoteStatus;
};

const inputClass =
  "w-full rounded-sm border border-border bg-background px-3 py-2 font-body text-sm text-foreground focus:border-primary focus:outline-none";
const buttonClass =
  "inline-flex items-center justify-center rounded-sm bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-foreground transition-opacity hover:opacity-90 disabled:opacity-50";
const ghostButtonClass =
  "inline-flex items-center justify-center rounded-sm border border-border px-3 py-2 font-display text-xs font-extrabold text-muted-foreground hover:text-foreground";

function AdminKeynotesPage() {
  const queryClient = useQueryClient();
  const loadSpeakers = useServerFn(listKeynoteSpeakersForAdmin);
  const loadPeople = useServerFn(listPeopleOptions);
  const saveSpeaker = useServerFn(saveKeynoteSpeaker);
  const removeSpeaker = useServerFn(deleteKeynoteSpeaker);
  const saveTopic = useServerFn(saveKeynoteTopic);
  const removeTopic = useServerFn(deleteKeynoteTopic);

  const speakersQuery = useQuery({ queryKey: ["admin-keynotes"], queryFn: () => loadSpeakers() });
  const peopleQuery = useQuery({ queryKey: ["admin-people-options"], queryFn: () => loadPeople() });

  const [newProfileId, setNewProfileId] = useState("");
  const [newSortOrder, setNewSortOrder] = useState("10");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-keynotes"] });

  const speakerMutation = useMutation({
    mutationFn: (input: SpeakerInput) => saveSpeaker({ data: input }),
    onSuccess: () => {
      invalidate();
      toast.success("Gespeichert.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const topicMutation = useMutation({
    mutationFn: (input: TopicInput) => saveTopic({ data: input }),
    onSuccess: () => {
      invalidate();
      toast.success("Gespeichert.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const speakers = speakersQuery.data ?? [];
  const people = peopleQuery.data?.people ?? [];
  const assigned = new Set(speakers.map((speaker) => speaker.profileId));

  return (
    <div className="space-y-8">
      <AdminPageHeader
        title="Keynotes"
        description="Referentinnen und Referenten für die Keynote-Seite und ihre Themen. Die Person selbst – Foto, Funktion, Kurztext – pflegen Sie im Teamprofil."
      />

      <section className="rounded-sm border border-border p-4">
        <h2 className="font-display text-base font-extrabold text-primary">Referent hinzufügen</h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="font-body text-sm text-muted-foreground">
            Teamprofil
            <select
              className={`${inputClass} mt-1 min-w-64`}
              value={newProfileId}
              onChange={(event) => setNewProfileId(event.target.value)}
            >
              <option value="">Bitte wählen</option>
              {people
                .filter((person) => !assigned.has(person.profileId))
                .map((person) => (
                  <option key={person.profileId} value={person.profileId}>
                    {personName(person.firstName, person.lastName)}
                    {person.isPublic ? "" : " (Profil verborgen)"}
                  </option>
                ))}
            </select>
          </label>
          <label className="font-body text-sm text-muted-foreground">
            Reihenfolge
            <input
              className={`${inputClass} mt-1 w-24`}
              value={newSortOrder}
              onChange={(event) => setNewSortOrder(event.target.value)}
            />
          </label>
          <button
            type="button"
            className={buttonClass}
            disabled={!newProfileId || speakerMutation.isPending}
            onClick={() => {
              speakerMutation.mutate({
                profileId: newProfileId,
                sortOrder: Number(newSortOrder) || 0,
                status: "entwurf",
              });
              setNewProfileId("");
            }}
          >
            Hinzufügen
          </button>
        </div>
        <p className="mt-3 font-body text-xs text-muted-foreground">
          Ein Referent erscheint auf der Seite nur, wenn er hier sichtbar ist <em>und</em> sein
          Teamprofil das Kennzeichen „auf der Website zeigen" trägt.
        </p>
      </section>

      {speakersQuery.isLoading ? (
        <p className="font-body text-sm text-muted-foreground">Wird geladen …</p>
      ) : speakers.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">
          Noch kein Referent hinterlegt.
        </p>
      ) : (
        speakers.map((speaker) => (
          <section key={speaker.id} className="rounded-sm border border-border p-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="font-display text-lg font-extrabold text-primary">{speaker.name}</h2>
                <p className="font-body text-sm text-muted-foreground">{speaker.jobTitle}</p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="font-body text-sm text-muted-foreground">
                  Reihenfolge
                  <input
                    className={`${inputClass} mt-1 w-24`}
                    defaultValue={String(speaker.sortOrder)}
                    onBlur={(event) =>
                      speakerMutation.mutate({
                        id: speaker.id,
                        profileId: speaker.profileId,
                        sortOrder: Number(event.target.value) || 0,
                        status: speaker.status,
                      })
                    }
                  />
                </label>
                <label className="font-body text-sm text-muted-foreground">
                  Sichtbarkeit
                  <select
                    className={`${inputClass} mt-1`}
                    value={speaker.status}
                    onChange={(event) =>
                      speakerMutation.mutate({
                        id: speaker.id,
                        profileId: speaker.profileId,
                        sortOrder: speaker.sortOrder,
                        status: event.target.value as KeynoteStatus,
                      })
                    }
                  >
                    {KEYNOTE_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {KEYNOTE_STATUS_LABELS[status]}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className={ghostButtonClass}
                  onClick={async () => {
                    if (!window.confirm(`${speaker.name} samt Themen entfernen?`)) return;
                    try {
                      await removeSpeaker({ data: { id: speaker.id } });
                      invalidate();
                    } catch (error) {
                      toast.error((error as Error).message);
                    }
                  }}
                >
                  Entfernen
                </button>
              </div>
            </div>

            <h3 className="mt-6 font-display text-sm font-extrabold text-foreground">Themen</h3>
            <div className="mt-3 space-y-4">
              {speaker.topics.map((topic) => (
                <div key={topic.id} className="rounded-sm border border-border/70 p-3">
                  <div className="grid gap-3 md:grid-cols-[1fr_6rem_10rem_auto] md:items-end">
                    <label className="font-body text-xs text-muted-foreground">
                      Überschrift
                      <input
                        className={`${inputClass} mt-1`}
                        defaultValue={topic.heading}
                        onBlur={(event) =>
                          topicMutation.mutate({
                            id: topic.id,
                            speakerId: speaker.id,
                            heading: event.target.value,
                            bodyMd: topic.bodyMd,
                            sortOrder: topic.sortOrder,
                            status: topic.status,
                          })
                        }
                      />
                    </label>
                    <label className="font-body text-xs text-muted-foreground">
                      Reihenfolge
                      <input
                        className={`${inputClass} mt-1`}
                        defaultValue={String(topic.sortOrder)}
                        onBlur={(event) =>
                          topicMutation.mutate({
                            id: topic.id,
                            speakerId: speaker.id,
                            heading: topic.heading,
                            bodyMd: topic.bodyMd,
                            sortOrder: Number(event.target.value) || 0,
                            status: topic.status,
                          })
                        }
                      />
                    </label>
                    <label className="font-body text-xs text-muted-foreground">
                      Sichtbarkeit
                      <select
                        className={`${inputClass} mt-1`}
                        value={topic.status}
                        onChange={(event) =>
                          topicMutation.mutate({
                            id: topic.id,
                            speakerId: speaker.id,
                            heading: topic.heading,
                            bodyMd: topic.bodyMd,
                            sortOrder: topic.sortOrder,
                            status: event.target.value as KeynoteStatus,
                          })
                        }
                      >
                        {KEYNOTE_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {KEYNOTE_STATUS_LABELS[status]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className={ghostButtonClass}
                      onClick={async () => {
                        if (!window.confirm("Thema endgültig löschen?")) return;
                        try {
                          await removeTopic({ data: { id: topic.id } });
                          invalidate();
                        } catch (error) {
                          toast.error((error as Error).message);
                        }
                      }}
                    >
                      Löschen
                    </button>
                  </div>
                  <label className="mt-3 block font-body text-xs text-muted-foreground">
                    Text
                    <textarea
                      rows={3}
                      className={`${inputClass} mt-1`}
                      defaultValue={topic.bodyMd}
                      onBlur={(event) =>
                        topicMutation.mutate({
                          id: topic.id,
                          speakerId: speaker.id,
                          heading: topic.heading,
                          bodyMd: event.target.value,
                          sortOrder: topic.sortOrder,
                          status: topic.status,
                        })
                      }
                    />
                  </label>
                </div>
              ))}
              <button
                type="button"
                className={buttonClass}
                onClick={() =>
                  topicMutation.mutate({
                    speakerId: speaker.id,
                    heading: "Neues Thema",
                    bodyMd: "",
                    sortOrder: (speaker.topics.at(-1)?.sortOrder ?? 0) + 10,
                    status: "entwurf",
                  })
                }
              >
                Thema hinzufügen
              </button>
            </div>
          </section>
        ))
      )}
    </div>
  );
}
